import { Button } from '@trackman/web-shared-components';
import { ServiceTypes } from 'constants/ServiceTypes';
import { SessionMode } from 'enums/SessionMode';
import { SessionType } from 'enums/SessionType';
import { observer } from 'mobx-react-lite';
import moment from 'moment';
import { GameDialog } from 'pages/ScheduledGames/GameDialog/GameDialog';
import React, { useState } from 'react';
import { useHistory } from 'react-router-dom';
import { FeatureConfigService } from 'services/FeatureConfigService';
import { useInjection } from 'services/ioc/ioc.react';
import { ContextualDataStore } from 'stores/ContextualDataStore';
import { FieldCalibrationStore, MAX_CALIBRATION_COUNT_NUMBER, MAX_HOME_PLATE_COUNT_NUMBER } from 'stores/FieldCalibrationStore';
import { NotificationStore } from 'stores/NotificationStore';
import { SessionStore } from 'stores/SessionStore';
import WizardStore from 'stores/WizardStore';
import { EndGameDialog } from '../EndGameDialog/EndGameDialog';
import {
  DualSystemBadge,
  ResumeGameControls,
  ResumeGameText,
  ResumeGameTile,
  StyledHomeMenuCard,
  TeamsAndExternalId,
  TextWrapper,
  TitleWrapper,
  Wrapper,
} from './GameInProgressStyles';

interface IProps {}

const GameInProgressCard: React.FC<IProps> = observer(() => {
  const sessionStore = useInjection<SessionStore>(ServiceTypes.SessionStore);
  const contextualDataStore = useInjection<ContextualDataStore>(ServiceTypes.ContextualDataStore);
  const [isEndGameButtonDisabled, setIsEndGameButtonDisabled] = useState<boolean>(false);
  const fieldCalibrationStore = useInjection<FieldCalibrationStore>(ServiceTypes.FieldCalibrationStore);
  const notificationStore = useInjection<NotificationStore>(ServiceTypes.NotificationStore);
  const wizardStore = useInjection<WizardStore>(ServiceTypes.WizardStore);
  const history = useHistory();
  const [isEndGameDialogVisible, setIsEndGameDialogVisible] = useState<boolean>(false);
  const { dualSystemReference } = sessionStore.currentSessionMetadata;
  const featureConfigService = useInjection<FeatureConfigService>(ServiceTypes.FeatureConfigService);

  const resumeHandler = (): void => {
    switch (sessionStore.currentSessionMode.sessionType) {
      case SessionType.BattingPractice:
        if (sessionStore.isMetadataLineupSet) {
          history.push('/tagging-bp');
        } else {
          contextualDataStore.setIsBpDialogVisible(true);
        }
        break;
      case SessionType.BouncingBallCalibration:
        if (
          fieldCalibrationStore.bounceWithin5FeetFromHPCount >= MAX_CALIBRATION_COUNT_NUMBER ||
          fieldCalibrationStore.hitHPCount >= MAX_HOME_PLATE_COUNT_NUMBER
        ) {
          fieldCalibrationStore.setCalibrationConfirmModalVisible(true);
          break;
        }

        history.push('/data-capture');
        break;
      default:
        if (sessionStore.isMetadataLineupSet) {
          history.push('/tagging');
        } else {
          contextualDataStore.setIsHomePageGameDialogVisible(true);
        }
        break;
    }
  };

  const endGameHandler = (): void => {
    setIsEndGameButtonDisabled(true);
    sessionStore
      .setCurrentSessionMode({
        ...sessionStore.currentSessionMode,
        sessionMode: SessionMode.SessionEnded,
      })
      .then((isSuccess: boolean) => {
        if (!isSuccess) {
          setIsEndGameButtonDisabled(false);
          notificationStore.addNotification({
            id: 'end-game-error',
            message: 'Error while changing session mode',
            variant: 'error',
            removable: true,
            onClose: () => {
              notificationStore.removeNotification('end-game-error');
            },
          });
        }
      })
      .finally(() => {
        let text = '';

        if (sessionStore.currentSessionMetadata.homeTeam?.name) {
          text += `${sessionStore.currentSessionMetadata.homeTeam.name}`;
        }

        if (sessionStore.currentSessionMetadata.awayTeam?.name) {
          text += ` vs ${sessionStore.currentSessionMetadata.awayTeam.name}`;
        }

        notificationStore.addToast({
          header: 'Game ended',
          message: `The ${text} game was successfully ended.`,
          severity: 'success',
          open: true,
          onClose: () => {
            notificationStore.removeToast();
          },
        });

        history.push('/games');
      });
    wizardStore.reset();
    fieldCalibrationStore.reset();
  };

  const getResumeHeader = (): string => {
    if (sessionStore.currentSessionMode.sessionType === SessionType.BouncingBallCalibration) {
      return 'Resume interrupted data capture';
    }

    return 'Game in progress';
  };

  const getResumeGameText = (): string | JSX.Element => {
    if (sessionStore.currentSessionMode.sessionType === SessionType.BouncingBallCalibration) {
      return (
        <>
          <div>
            The data capture for calibration initiated at <b>{moment(sessionStore.currentSessionMetadata.time).format('HH:mm')}</b> on the{' '}
            <b>{moment(sessionStore.currentSessionMetadata.time).format('DD-MM-YYYY')}</b> was
          </div>
          <div>interrupted. If you don't take any action it will be automatically archived after 24 hours.</div>
        </>
      );
    }
    if (sessionStore.currentSessionMode.sessionType === SessionType.BattingPractice) {
      return (
        <>
          A batting practice{sessionStore.isMetadataLineupSet ? ` for ${sessionStore.currentSessionMetadata.homeTeam?.name ?? ''}` : ''} has
          started. If you don't take any action it will be automatically archived after 24 hours.
        </>
      );
    }

    if (sessionStore.currentSessionMode.gameScheduleReference && sessionStore.currentSessionMetadata.isOperatorlessTaggingEnabled) {
      return <>Auto-tag is enabled on this game, end the game to access the schedule.</>;
    }

    return <>Enter the game to to tag it, or end the game to access the schedule.</>;
  };

  const onEndGameClick = () => {
    if (sessionStore.currentSessionMode.sessionType === SessionType.BouncingBallCalibration) {
      fieldCalibrationStore.setAbortModalVisible(true);

      return;
    }

    setIsEndGameDialogVisible(true);
  };

  const getTeamsAndExternalId = () => {
    let text = '';

    if (sessionStore.currentSessionMetadata.homeTeam?.name) {
      text += `${sessionStore.currentSessionMetadata.homeTeam.name}`;
    }

    if (sessionStore.currentSessionMetadata.awayTeam?.name) {
      text += ` vs ${sessionStore.currentSessionMetadata.awayTeam.name}`;
    }

    if (sessionStore.currentSessionMetadata.externalSessionId) {
      text += ` - ${sessionStore.currentSessionMetadata.externalSessionId}`;
    }

    return text;
  };

  const getDualSystemBadge = () => {
    if (!featureConfigService.getConfig().isDualSystemArmingEnabled) {
      return undefined;
    }

    if (dualSystemReference?.masterSessionId) {
      return <DualSystemBadge>Dual System Active</DualSystemBadge>;
    } else {
      return <DualSystemBadge disabled>Dual System Inactive</DualSystemBadge>;
    }
  };

  return (
    <Wrapper>
      <StyledHomeMenuCard>
        <TextWrapper>
          {getDualSystemBadge()}
          <TitleWrapper>
            <ResumeGameTile>{getResumeHeader()}</ResumeGameTile>
            <TeamsAndExternalId>{getTeamsAndExternalId()}</TeamsAndExternalId>
          </TitleWrapper>
          <ResumeGameText>{getResumeGameText()}</ResumeGameText>
        </TextWrapper>
        <ResumeGameControls>
          <Button
            intent='secondary'
            width='89px'
            label='End Game'
            disabled={isEndGameButtonDisabled}
            onClick={onEndGameClick}
            data-testid='end-game-home-button'
          />
          {!sessionStore.currentSessionMetadata.isOperatorlessTaggingEnabled && (
            <Button intent='primary' width='112px' label='Resume Game' onClick={resumeHandler} data-testid='resume-game-home-button' />
          )}
        </ResumeGameControls>
      </StyledHomeMenuCard>
      <GameDialog
        visible={contextualDataStore.isHomePageGameDialogVisible}
        dismissCallback={(): void => contextualDataStore.setIsHomePageGameDialogVisible(false)}
        gameScheduleReference={sessionStore.currentSessionMode.gameScheduleReference}
        strikeZones={contextualDataStore.strikeZones}
        isResumeGame
      />
      <EndGameDialog
        open={isEndGameDialogVisible}
        onEndGame={endGameHandler}
        onResumeGame={resumeHandler}
        onClose={() => setIsEndGameDialogVisible(false)}
        isAutoTagEnabled={sessionStore.currentSessionMetadata.isOperatorlessTaggingEnabled}
      />
    </Wrapper>
  );
});

export default GameInProgressCard;
